/**
 * Map debugging utilities for drawing diagnostic overlays
 * Uses deck.gl overlay for rendering points, lines and text
 */

import { LineLayer, TextLayer } from '@deck.gl/layers';
import { MapboxOverlay } from '@deck.gl/mapbox';

let mapInstance = null;
let overlay = null;
let drawQueue = [];

function hexToRgba(hex, alpha = 255) {
  if (typeof hex !== 'string') return hex;
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return [r, g, b, alpha];
}

export function registerMap(map) {
  mapInstance = map;
  overlay = new MapboxOverlay({ layers: [] });
  map.addControl(overlay);
}

export function drawPoint(latLon, options = {}) {
  const { color = '#ff0000', radius = 6 } = options;
  drawQueue.push({
    type: 'point',
    latLon,
    color: hexToRgba(color),
    radius
  });
}

export function drawLine(points, options = {}) {
  const { color = '#00ff00', width = 2 } = options;
  drawQueue.push({
    type: 'line',
    points,
    color: hexToRgba(color),
    width
  });
}

export function drawText(latLon, text, options = {}) {
  const { color = '#000000', size = 14 } = options;
  drawQueue.push({
    type: 'text',
    latLon,
    text: String(text),
    color: hexToRgba(color),
    size
  });
}

export function renderAndClear() {
  if (!overlay) return;

  const lineData = [];
  const textData = [];

  for (const item of drawQueue) {
    if (item.type === 'line') {
      for (let i = 0; i < item.points.length - 1; i++) {
        lineData.push({
          sourcePosition: [item.points[i].lon, item.points[i].lat],
          targetPosition: [item.points[i + 1].lon, item.points[i + 1].lat],
          color: item.color,
          width: item.width
        });
      }
    } else if (typeof item.latLon == 'undefined' || item.latLon == null || typeof item.latLon.lon == 'undefined' || item.latLon.lon == null) {
      continue;
    } else if (item.type === 'point') {
      const offset = 0.0001;
      lineData.push({
        sourcePosition: [item.latLon.lon, item.latLon.lat],
        targetPosition: [item.latLon.lon + offset, item.latLon.lat + offset],
        color: item.color,
        width: item.radius * 2
      });
    } else if (item.type === 'text') {
      textData.push({
        position: [item.latLon.lon, item.latLon.lat],
        text: item.text,
        color: item.color,
        size: item.size
      });
    }
  }

  const layers = [];

  if (lineData.length > 0) {
    layers.push(
      new LineLayer({
        id: 'debug-overlay',
        data: lineData,
        getSourcePosition: d => d.sourcePosition,
        getTargetPosition: d => d.targetPosition,
        getColor: d => d.color,
        getWidth: d => d.width,
        widthMinPixels: 2
      })
    );
  }

  if (textData.length > 0) {
    layers.push(
      new TextLayer({
        id: 'debug-text',
        data: textData,
        getPosition: d => d.position,
        getText: d => d.text,
        getSize: d => d.size,
        getColor: d => d.color,
        outlineColor: [255, 255, 255, 255],
        outlineWidth: 2,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center',
        fontFamily: 'monospace',
        characterSet: 'auto'
      })
    );
  }

  overlay.setProps({ layers });
  drawQueue = [];
}
